<template>
  <v-container id="user_profile_root" fluid>
    <!-- DIALOG -->
    <router-view name="dialog"></router-view>
    <!-- CONTENT -->
    <v-container fluid>
      <v-row lass="mb-6 px-6" align="center" justify="center">
        <v-col>
          <span class="text-h5">
            {{ $t('users.title.profile') }}
            <v-spacer></v-spacer>
          </span>
        </v-col>
      </v-row>

      <v-row>
        <!-- SETTINGS -->
        <v-col>
          <v-card class="pa-6">
            <v-card-title color="black--text">
              <v-icon color="black" class="mr-2">mdi-account-outline</v-icon>
              {{ $t("users.content.settings.general") }}
            </v-card-title>
            <!-- <span class="text-h6">
              <v-icon color="black">mdi-account-outline</v-icon>
              {{ $t("users.settings.general") }}
            </span> -->
            <v-list-item>
              <v-list-item-content>
                <v-form ref="userProfileForm" v-model="userProfileFormValid">
                  <v-text-field
                    id="firstname" v-model="user.firstName" :label="$t('users.content.firstName') + `*`"
                    :rules="[rules.required, rules.minMax]"
                  ></v-text-field>
                  <v-text-field
                    id="lastname" v-model="user.lastName" :label="$t('users.content.lastName') + `*`"
                    :rules="[rules.required, rules.minMax]"
                  ></v-text-field>
                  <v-btn
                    v-if="accountManagementURL" id="accountManagement" text color="primary" target="_blank" :href="accountManagementURL"
                  >
                    <v-icon class="mr-1">mdi-cog-outline</v-icon>
                    {{ $t('users.content.manageAccount') }}
                  </v-btn>
                  <v-btn
                    id="update" class="mt-3 mb-3" color="primary" left :disabled="updateMyUserDisabled"
                    @click="updateMyUser"
                  >
                    <v-icon>mdi-progress-pencil</v-icon>
                    {{ $t('main.update') }}
                  </v-btn>
                </v-form>
              </v-list-item-content>
            </v-list-item>

            <v-card-title color="black--text">
              <v-icon color="black" class="mr-2">mdi-map</v-icon>
              {{ $t('users.content.language') }}
            </v-card-title>
            <v-list-item>
              <v-list-item-content>
                <v-form ref="userLanguageForm">
                  <v-select
                    id="language" v-model="selectedLanguage" :items="languageOptions"
                    :label="$t('users.content.language')"
                  ></v-select>
                </v-form>
              </v-list-item-content>
            </v-list-item>
            <!-- <v-list-item>
              <v-list-item-content>
                <div>
                  <span>{{ $t('users.content.roles.label') }}</span>
                  <v-chip-group column>
                    <v-chip v-for="role in rdssRoles" :key="role.value">{{ $t(role.text) }}</v-chip>
                  </v-chip-group>
                </div>
              </v-list-item-content>
            </v-list-item> -->

            <template v-if="deleteAccountURL">
              <v-divider class="my-6"></v-divider>
              <v-card-title color="black--text">
                <v-icon color="black" class="mr-2">mdi-close-octagon</v-icon>
                {{ $t('users.content.deleteMyAccount') }}
              </v-card-title>
              <v-card-subtitle class="ma-6 subtitle-1">
                {{ $t('users.content.descriptionDeleteMyAccount') }}
              </v-card-subtitle>
              <v-btn id="deleteAccount" outlined text color="error" @click="confirmDeleteMyAccount">
                <v-icon class="mr-1">mdi-delete-outline</v-icon>
                {{ $t('users.content.deleteMyAccount') }}
              </v-btn>
            </template>
          </v-card>
        </v-col>

        <!-- <v-col v-if="!sealEnabled || showDevices"> -->
        <v-col v-if="showDevices">
          <v-card class="pa-6">
            <v-card-title>
              <v-icon color="black" class="mr-2">mdi-cellphone-key</v-icon>
              {{ $t('dashboard.devices') }}
            </v-card-title>

            <v-data-table
              id="devices_table" must-sort :headers="headers" hide-default-header hide-default-footer
              :items="deviceList" :options.sync="pagination" :server-items-length="devicesListTotalItems"
              :loading="dataTableLoading" :loading-text="$t('dataTable.content.loadingPleaseWait')"
              :no-data-text="$t('dataTable.content.noDataAvailable')"
            >
              <template #[`item.status`]="{ item }">
                <v-chip label :color="getChipStyle(item)">
                  {{ getChipText(item) }}
                </v-chip>
              </template>
              <template #[`item.actions`]="{ item }">
                <v-btn
                  id="devices_table_manage" text class="pa-1 "
                  :to="'/user/profile/devices/' + item.id + '/update'"
                >
                  <v-icon>
                    mdi-update
                  </v-icon>
                  {{ $t('dataTable.content.devices.manage') }}
                </v-btn>

                <v-btn id="devices_table_delete" text class="pa-1 " @click="deleteDevice(item.id, $event)">
                  <v-icon>
                    mdi-delete
                  </v-icon>
                  {{ $t('dataTable.content.devices.delete') }}
                </v-btn>
              </template>
            </v-data-table>

            <v-btn id="devices_table_add" text color="primary" to="/user/profile/devices/add">
              <v-icon>mdi-plus</v-icon>
              {{ $t('dashboard.addDevice') }}
            </v-btn>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-container>
</template>

<script>
import seal from '@/services/api/seal.js'
import debounce from 'lodash.debounce'
import { mapGetters } from 'vuex'
import Role from '../../Role'
import { showConfirmationMenu, showToastError, showToastSuccess } from '../../event-bus'
import eventTypes from '../../eventTypes'
import credentials from '../../services/api/credentials'
import devices from '../../services/api/devices'
import rdssUtil from '../../services/api/rdssUtil'
import users from '../../services/api/users'
import CommonError from '../CommonError'
import language from './Language'
import UserStatus from './UserStatus'
import defaultRoles from './defaultRoles'

export default {
  name: 'UserProfile',

  data() {
    return {
      userProfileFormValid: false,
      maxCharacters: 255,
      user: {},

      selectedLanguage: language.EN,
      switchUserStatus: false,
      userStatusList: UserStatus,
      setDistinguishedName: false,
      distinguishedName: {},
      rules: {
        required: v => (!!v || !isNaN(parseFloat(v))) || this.$t('validation.form.requiredField'),
        minMax: v => (v && v.length <= this.maxCharacters) || this.$t('validation.form.mustBeLessThan', { max: this.maxCharacters }),
        email: v => !v || /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(v) || this.$t('validation.form.emailMustBeValid')
      },
      pagination: {
        sortBy: ['friendlyName']
      },
      deviceList: [],
      devicesListTotalItems: 0,
      sealStatus: {
        enabled: false,
        secureMode: false,
        activated: false
      },
      // sealActivated: this.$store.state.remoteConfig && this.$store.state.remoteConfig.sealSign,
      // showDevices: false,
      showDevices: true,
      accountManagementURL: this.$store.state.remoteConfig && this.$store.state.remoteConfig.accountManagementURL,
      changePasswordURL: this.$store.state.remoteConfig && this.$store.state.remoteConfig.changePasswordURL,
      deleteAccountURL: this.$store.state.remoteConfig && this.$store.state.remoteConfig.deleteAccountURL,
      dataTableLoading: false,
      headers: [
        {
          text: this.$t('dataTable.header.devices.friendlyName'),
          align: 'left',
          value: 'friendlyName'
        },
        {
          text: this.$t('dataTable.header.devices.status'),
          align: 'left',
          value: 'status',
          sortable: false
        },
        {
          text: this.$t('dataTable.header.devices.user'),
          align: 'left',
          value: 'user.name'
        },
        {
          text: this.$t('dataTable.header.actions'),
          align: 'right',
          value: 'actions',
          sortable: false
        }
      ],
      rdssRoles: []
    }
  },

  computed: {
    ...mapGetters(['hasRole']),
    isAdmin() {
      return this.hasRole(Role.ADMIN)
    },

    languageOptions() {
      return [
        { value: language.EN, text: this.$t('users.content.languages.English') },
        { value: language.HU, text: this.$t('users.content.languages.Hungarian') }
      ]
    },

    sealActivated() {
      return this.sealStatus.enabled && this.sealStatus.activated
    },

    sealEnabled() {
      return this.sealStatus.enabled
    },
    hasRdss() {
      return !!this.$store.state.remoteConfig.rdssApiUrl
    },
    updateMyUserDisabled() {
      const { firstName, lastName } = this.$store.state.currentUser
      return this.user.firstName === firstName && this.user.lastName === lastName
    }
  },

  watch: {
    search() {
      this.dataTableLoading = true
      this.searchDeviceWatcher()
    },

    pagination() {
      this.searchDevices()
    },

    selectedLanguage(newVal, oldVal) {
      this.updateMyUser()
    }
  },

  async mounted() {
    this.sealStatus = await seal.getSealStatus()
    // console.log('sealStatus', this.sealStatus)
    // removed sincse SIC new features
    /* if (this.sealStatus.enabled) {
      await this.checkCredentials()
    } else {
      this.showDevices = true
    } */
    this.initDeviceEventSource()

    await this.getMyUser()
  },

  methods: {
    searchDeviceWatcher: debounce(function() {
      this.searchDevices()
    }, 500),

    async searchDevices() {
      this.dataTableLoading = true
      try {
        const pageRequest = {
          'page-num': this.pagination.page,
          'page-size': this.pagination.itemsPerPage,
          'sort-field': this.pagination.sortBy[0],
          'sort-order': this.pagination.sortDesc[0] ? 'desc' : 'asc'
        }

        const page = await devices.listMyDevices(this.search, pageRequest)
        this.deviceList = page.content
        this.devicesListTotalItems = page.total
      } catch (error) {
        const errorMsg = this.getErrorMessage(error)
        showToastError(errorMsg)
      } finally {
        this.dataTableLoading = false
      }
    },

    async updateMyUser() {
      if (!this.$refs.userProfileForm.validate()) {
        return
      }
      try {
        this.user.locale = this.selectedLanguage
        await users.updateMyUser(this.user)
        this.$store.commit('setCurrentUser', this.user)
        if (this.hasRdss) {
          await rdssUtil.updateMyUserOnRdss(this.user)
        }
        this.setLanguage(this.selectedLanguage)
        showToastSuccess(this.$t('toastMessage.succesfullyUpdated'))
      } catch (error) {
        const errorMsg = this.getErrorMessage(error)
        showToastError(errorMsg)
      }
    },

    async getMyUser() {
      try {
        this.user = await users.getMyUser()
        this.setDefaultValuesByGetMyUser()
        if (this.hasRdss) {
          await rdssUtil.joinUserOnRdss()
          const rdssUser = await rdssUtil.getUserOnRdss()
          this.rdssRoles = rdssUser.roles.map(r => { return { value: r, text: defaultRoles.find(x => x.value === r).displayTextJsonKey } })
        }
      } catch (error) {
        const errorMsg = this.getErrorMessage(error)
        showToastError(errorMsg)
      }
    },

    setDefaultValuesByGetMyUser() {
      this.switchUserStatus = !!this.user.status.includes(UserStatus.ENABLED)
      this.selectedLanguage = this.user.locale
    },

    async logout() {
      await this.$auth.logout()
    },

    back() {
      this.$router.go(-1)
    },

    async deleteDevice(id, e) {
      const opts = {
        title: this.$t('dialog.confirmation.title'),
        posX: e.clientX,
        posY: e.clientY
      }
      const conf = await showConfirmationMenu(opts)
      if (!conf) {
        return
      }

      try {
        await devices.deleteDevice(id)
        this.searchDevices()
        showToastSuccess(this.$t('toastMessage.succesfullyDeleted'))
      } catch (error) {
        let errorMsg = this.$t('toastMessage.errorWhileRetrievingData')
        if ((error.response !== undefined) && (error.response.data.error !== undefined)) {
          const splittedErrorMessage = error.response.data.error_description.split(' ')
          if (error.response.data.error === CommonError.STILL_REFERENCED) {
            errorMsg = this.$t('toastMessage.stillReferenced', { 0: splittedErrorMessage[0], 1: splittedErrorMessage[5] })
          } else {
            errorMsg = this.getErrorMessage(error)
          }
        } else {
          errorMsg = this.getErrorMessage(error)
        }
        showToastError(errorMsg)
      }
    },

    getChipStyle(item) {
      if (item.enabled === true) {
        return 'enabled'
      } else {
        return 'inactive'
      }
    },
    getChipText(item) {
      if (item.enabled === true) {
        return this.$t('dataTable.header.devices.states.enabled')
      } else {
        return this.$t('dataTable.header.devices.states.inactive')
      }
    },

    async checkCredentials() {
      const resp = await credentials.listMyActivatedCredentials()
      // console.log('checkCredentials', resp.content)
      const total = resp.content.filter(elem => ((elem.keyPurpose === 'ASYMMETRIC_SIGN') && (elem.authMode === 'IMPLICIT'))).length
      this.showDevices = total > 0
      // this.showDevices = true
    },
    async confirmDeleteMyAccount(e) {
      const opts = {
        title: this.$t('dialog.confirmation.title'),
        posX: e.clientX,
        posY: e.clientY
      }
      const conf = await showConfirmationMenu(opts)
      if (!conf) {
        return
      }
      window.location.href = this.deleteAccountURL
    },
    async initDeviceEventSource() {
      const token = await this.$auth.getIdToken()

      const eventSourceURL = this.$store.state.remoteConfig.rsspApiUrl + '/rssp/v1/events'
      let sse
      try {
        // eslint-disable-next-line
          sse = new EventSourcePolyfill(eventSourceURL, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        })
      } catch (e) {
        console.error(e)
      }

      sse.addEventListener(eventTypes.DEVICE_CREATED, (event) => {
        this.searchDeviceWatcher()
      })
      sse.addEventListener(eventTypes.DEVICE_UPDATED, (event) => {
        this.searchDeviceWatcher()
      })
      sse.addEventListener(eventTypes.DEVICE_DELETED, (event) => {
        this.searchDeviceWatcher()
      })
    }
  }
}
</script>
